import type {
  AriaAttributes,
  CSSProperties,
  ElementType,
  ReactNode,
  Ref,
} from 'react';
import { Children, cloneElement, isValidElement } from 'react';

import classNames from 'classnames';

import styles from './ScrollSnap.module.css';

export interface ScrollSnapProps extends AriaAttributes {
  children: ReactNode;
  /** The HTML element to render. Default is `div` */
  as?: ElementType;
  /** Optional class name */
  className?: string;
  style?: CSSProperties;
  id?: string;
  onScroll?: JSX.IntrinsicElements['div']['onScroll'];
  /** A ref to forward to the container element (the scrollable element). */
  containerRef?: Ref<HTMLElement>;
}

/**
 * Create a flexbox container where the children are scrolled horizontally,
 * snapping to their center.
 *
 * See {@link https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Scroll_Snap }
 * for more details
 *
 * @see https://corgi-x.tfd.engineering/components/scrollnap
 */
export function ScrollSnap(props: ScrollSnapProps) {
  const {
    children,
    as: Component = 'div',
    className,
    containerRef,
    ...restProps
  } = props;
  return (
    <Component
      ref={containerRef}
      className={classNames(styles.container, className)}
      {...restProps}
    >
      {Children.map(children, child => {
        if (!isValidElement<{ className: string }>(child)) {
          return child;
        }

        return cloneElement(child, {
          className: classNames(child.props.className, styles.item),
        });
      })}
    </Component>
  );
}
